import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeft, Plus, Trash2, Calendar, Check, X, Clock, FileText, ChevronDown, ChevronUp, Filter, ChevronLeft, ChevronRight, Grid, List } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminWeekendCoursesPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [weekendCourses, setWeekendCourses] = useState([]);
  const [datePeriods, setDatePeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDatePeriods, setShowDatePeriods] = useState(false);
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all' or 'open'
  const [monthsView, setMonthsView] = useState(4); // 1, 2 or 4
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0); // For mobile swipe navigation
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [newPeriod, setNewPeriod] = useState({
    start_date: '',
    num_days: 4,
    description: ''
  });
  const [assignDatesModal, setAssignDatesModal] = useState({ 
    show: false, 
    course: null, 
    dates: '',
    bookedStartDate: '',
    bookedEndDate: ''
  });

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe handlers for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (monthKeys) => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0 && currentMonthIndex < monthKeys.length - 1) {
        // Swipe left - next month
        setCurrentMonthIndex(prev => prev + 1);
      } else if (swipeDistance < 0 && currentMonthIndex > 0) {
        // Swipe right - previous month
        setCurrentMonthIndex(prev => prev - 1);
      }
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email)) {
      fetchWeekendCourses();
      fetchDatePeriods();
    }
  }, [user]);

  const fetchWeekendCourses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/weekend-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const externalCourses = response.data.filter(c => c.booking_type === 'weekend-external');
      setWeekendCourses(externalCourses);
    } catch (error) {
      console.error('Error fetching weekend courses:', error);
      toast.error('Kunne ikke hente weekend kurser');
    } finally {
      setLoading(false);
    }
  };

  const fetchDatePeriods = async () => {
    try {
      const response = await axios.get(`${API}/admin/weekend-date-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDatePeriods(response.data);
    } catch (error) {
      console.error('Error fetching date periods:', error);
    }
  };

  // Check if a period has any approved bookings
  const getPeriodBookingStatus = (periodId) => {
    const bookings = weekendCourses.filter(c => 
      c.selected_date_period === periodId && 
      (c.status === 'approved' || c.status === 'processed')
    );
    const approvedCount = weekendCourses.filter(c => 
      c.selected_date_period === periodId && c.status === 'approved'
    ).length;
    
    return {
      hasBookings: bookings.length > 0,
      approvedCount,
      pendingCount: bookings.length - approvedCount,
      isFullyBooked: approvedCount > 0 // Simple logic - can be expanded
    };
  };

  // Filter periods based on filter selection
  const getFilteredPeriods = () => {
    let filtered = [...datePeriods];
    
    if (periodFilter === 'open') {
      filtered = filtered.filter(p => {
        const status = getPeriodBookingStatus(p.id);
        return !status.isFullyBooked && p.is_active;
      });
    }
    
    // Sort by start date
    filtered.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    
    return filtered;
  };

  // Group periods by month
  const groupPeriodsByMonth = () => {
    const filtered = getFilteredPeriods();
    const grouped = {};
    
    filtered.forEach(period => {
      const date = new Date(period.start_date + 'T00:00:00');
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = { name: monthName, periods: [] };
      }
      grouped[monthKey].periods.push(period);
    });
    
    return grouped;
  };

  // Generate calendar days for a month
  const generateCalendarDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week for first day (0 = Sunday, 1 = Monday, etc.)
    // Adjust for Monday start (0 = Monday)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6; // Sunday becomes 6
    
    const days = [];
    
    // Add empty cells for days before first of month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      days.push({ day, date: dateStr });
    }
    
    return days;
  };

  // Check if a date falls within any period
  const getDateStatus = (dateStr) => {
    if (!dateStr) return null;
    
    const filtered = getFilteredPeriods();
    
    for (const period of filtered) {
      const startDate = new Date(period.start_date + 'T00:00:00');
      const endDate = new Date(period.end_date + 'T00:00:00');
      const checkDate = new Date(dateStr + 'T00:00:00');
      
      if (checkDate >= startDate && checkDate <= endDate) {
        const status = getPeriodBookingStatus(period.id);
        return {
          period,
          isStart: dateStr === period.start_date,
          isEnd: dateStr === period.end_date,
          isBooked: status.isFullyBooked,
          isOpen: !status.isFullyBooked && period.is_active
        };
      }
    }
    return null;
  };

  // Get months for calendar view
  const getCalendarMonths = () => {
    const months = [];
    const today = new Date();
    
    // Show 6 months from current month
    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      months.push({
        year: date.getFullYear(),
        month: date.getMonth(),
        name: date.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' }),
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      });
    }
    return months;
  };

  const handleAddPeriod = async () => {
    if (!newPeriod.start_date) {
      toast.error('Vælg en start dato');
      return;
    }

    // Calculate end date based on start date and num_days
    const startDate = new Date(newPeriod.start_date + 'T00:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + newPeriod.num_days - 1);
    const endDateStr = endDate.toISOString().split('T')[0];

    // Auto-generate description if not provided
    const description = newPeriod.description || 
      `${startDate.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })} - ${endDate.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}`;

    try {
      await axios.post(`${API}/admin/weekend-date-periods`, {
        start_date: newPeriod.start_date,
        end_date: endDateStr,
        max_days: newPeriod.num_days,
        description: description
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dato periode tilføjet');
      setShowAddPeriod(false);
      setNewPeriod({ start_date: '', num_days: 4, description: '' });
      fetchDatePeriods();
    } catch (error) {
      toast.error('Kunne ikke tilføje periode');
    }
  };

  const handleDeletePeriod = async (periodId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne periode?')) return;
    
    try {
      await axios.delete(`${API}/admin/weekend-date-periods/${periodId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Periode slettet');
      fetchDatePeriods();
    } catch (error) {
      toast.error('Kunne ikke slette periode');
    }
  };

  const handleTogglePeriod = async (periodId, isActive) => {
    try {
      await axios.put(`${API}/admin/weekend-date-periods/${periodId}?is_active=${!isActive}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isActive ? 'Periode deaktiveret' : 'Periode aktiveret');
      fetchDatePeriods();
    } catch (error) {
      toast.error('Kunne ikke opdatere periode');
    }
  };

  const handleUpdateCourseStatus = async (courseId, status, assignedDates = null, bookedStartDate = null, bookedEndDate = null) => {
    try {
      let url = `${API}/admin/weekend-courses/${courseId}/status?status=${status}`;
      if (assignedDates) {
        url += `&assigned_dates=${encodeURIComponent(assignedDates)}`;
      }
      if (bookedStartDate) {
        url += `&booked_start_date=${bookedStartDate}`;
      }
      if (bookedEndDate) {
        url += `&booked_end_date=${bookedEndDate}`;
      }
      
      await axios.put(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const statusText = {
        'processed': 'behandlet',
        'approved': 'accepteret', 
        'rejected': 'afvist'
      };
      
      toast.success(`Kursus markeret som ${statusText[status]}`);
      fetchWeekendCourses();
      fetchDatePeriods(); // Refresh periods to update availability
      setAssignDatesModal({ show: false, course: null, dates: '', bookedStartDate: '', bookedEndDate: '' });
    } catch (error) {
      toast.error('Kunne ikke opdatere status');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne anmodning?')) return;
    
    try {
      await axios.delete(`${API}/admin/weekend-courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Anmodning slettet');
      fetchWeekendCourses();
    } catch (error) {
      toast.error('Kunne ikke slette anmodning');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('da-DK', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('da-DK', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getPeriodDescription = (periodId) => {
    const period = datePeriods.find(p => p.id === periodId);
    return period ? period.description || `${formatDate(period.start_date)} - ${formatDate(period.end_date)}` : 'Ukendt periode';
  };

  // Calculate suggested dates based on customer's request
  const getSuggestedDates = (course) => {
    if (!course?.selected_date_period) return '';
    
    const period = datePeriods.find(p => p.id === course.selected_date_period);
    if (!period) return '';
    
    const startDate = new Date(period.start_date + 'T00:00:00');
    const endDate = new Date(period.end_date + 'T00:00:00');
    const numDays = course.num_days || 2;
    const preference = course.day_preference || 'first';
    
    let resultStart, resultEnd;
    
    if (preference === 'first') {
      resultStart = new Date(startDate);
      resultEnd = new Date(startDate);
      resultEnd.setDate(resultStart.getDate() + numDays - 1);
    } else {
      // 'last' - count back from end
      resultEnd = new Date(endDate);
      resultStart = new Date(endDate);
      resultStart.setDate(resultEnd.getDate() - numDays + 1);
    }
    
    // Format as "13-16. aug. 2026"
    const startDay = resultStart.getDate();
    const endDay = resultEnd.getDate();
    const month = resultEnd.toLocaleDateString('da-DK', { month: 'short' });
    const year = resultEnd.getFullYear();
    
    return {
      displayText: `${startDay}-${endDay}. ${month} ${year}`,
      startDate: resultStart.toISOString().split('T')[0],
      endDate: resultEnd.toISOString().split('T')[0]
    };
  };

  // Open modal with pre-filled dates
  const openAcceptModal = (course) => {
    const suggested = getSuggestedDates(course);
    setAssignDatesModal({ 
      show: true, 
      course, 
      dates: suggested.displayText || '',
      bookedStartDate: suggested.startDate || '',
      bookedEndDate: suggested.endDate || ''
    });
  };

  // Open modal with empty date field (for custom date)
  const openAcceptModalWithCustomDate = (course) => {
    setAssignDatesModal({ 
      show: true, 
      course, 
      dates: '',
      bookedStartDate: '',
      bookedEndDate: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      requested: { bg: '#fff3e0', color: '#f57c00', text: 'Afventer' },
      processed: { bg: '#e3f2fd', color: '#1976d2', text: 'Behandlet' },
      approved: { bg: '#e8f5e9', color: '#2e7d32', text: 'Accepteret' },
      rejected: { bg: '#ffebee', color: '#c62828', text: 'Afvist' }
    };
    const style = styles[status] || styles.requested;
    return (
      <span style={{
        padding: '0.35rem 0.85rem',
        borderRadius: '16px',
        fontSize: '0.8rem',
        fontWeight: 600,
        background: style.bg,
        color: style.color
      }}>
        {style.text}
      </span>
    );
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  const groupedPeriods = groupPeriodsByMonth();
  const monthKeys = Object.keys(groupedPeriods).sort();

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div className="admin-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          {/* Header */}
          <div style={{ 
            background: 'white', 
            padding: '1.5rem 2rem', 
            borderRadius: '12px', 
            marginBottom: '1.5rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginBottom: '0.75rem',
                padding: '0.5rem',
                borderRadius: '5px'
              }}
            >
              <ArrowLeft size={18} />
              Tilbage
            </button>
            <h1 style={{ marginBottom: '0.25rem', fontSize: '1.75rem', color: '#333' }}>
              Weekend Kurser - Udkørende
            </h1>
          </div>

          {/* Collapsible Date Periods Section */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            marginBottom: '1.5rem',
            overflow: 'hidden'
          }}>
            {/* Header - Always visible */}
            <button
              onClick={() => setShowDatePeriods(!showDatePeriods)}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.25rem 1.5rem',
                background: showDatePeriods ? '#f8f6f3' : 'white',
                border: 'none',
                cursor: 'pointer',
                borderBottom: showDatePeriods ? '1px solid #e5e0d8' : 'none'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Calendar size={22} color="#7c6f5e" />
                <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                  Tilgængelige Dato Perioder
                </span>
                <span style={{
                  background: '#e8f5e9',
                  color: '#2e7d32',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {datePeriods.filter(p => p.is_active).length} aktive
                </span>
              </div>
              {showDatePeriods ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
            </button>

            {/* Expandable Content */}
            {showDatePeriods && (
              <div style={{ padding: '1.5rem' }}>
                {/* Toolbar */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1.25rem',
                  flexWrap: 'wrap',
                  gap: '0.75rem'
                }}>
                  {/* Filters */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Filter size={16} color="#888" />
                    <button
                      onClick={() => setPeriodFilter('all')}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: periodFilter === 'all' ? '#7c6f5e' : '#f0f0f0',
                        color: periodFilter === 'all' ? 'white' : '#555',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Alle
                    </button>
                    <button
                      onClick={() => setPeriodFilter('open')}
                      style={{
                        padding: '0.4rem 0.8rem',
                        background: periodFilter === 'open' ? '#4caf50' : '#f0f0f0',
                        color: periodFilter === 'open' ? 'white' : '#555',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Kun åbne
                    </button>
                    
                    {/* Separator and month selector - only show on desktop */}
                    {!isMobile && (
                      <>
                        <span style={{ color: '#ccc', margin: '0 0.5rem' }}>|</span>
                    
                        {/* Months view selector */}
                        {[1, 2, 4].map(num => (
                          <button
                            key={num}
                            onClick={() => setMonthsView(num)}
                            style={{
                              padding: '0.4rem 0.6rem',
                              background: monthsView === num ? '#7c6f5e' : '#f0f0f0',
                              color: monthsView === num ? 'white' : '#555',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              minWidth: '32px'
                            }}
                          >
                            {num}m
                          </button>
                        ))}
                        
                        <span style={{ color: '#ccc', margin: '0 0.5rem' }}>|</span>
                        
                        {/* View mode toggle */}
                        <button
                          onClick={() => setViewMode('list')}
                          title="Liste visning"
                          style={{
                            padding: '0.4rem',
                            background: viewMode === 'list' ? '#7c6f5e' : '#f0f0f0',
                            color: viewMode === 'list' ? 'white' : '#555',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          <List size={16} />
                        </button>
                        <button
                          onClick={() => setViewMode('calendar')}
                          title="Kalender visning"
                          style={{
                            padding: '0.4rem',
                            background: viewMode === 'calendar' ? '#7c6f5e' : '#f0f0f0',
                            color: viewMode === 'calendar' ? 'white' : '#555',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          <Grid size={16} />
                        </button>
                      </>
                    )}
                    
                    {/* Mobile view mode toggle */}
                    {isMobile && (
                      <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
                        <button
                          onClick={() => setViewMode('list')}
                          style={{
                            padding: '0.4rem',
                            background: viewMode === 'list' ? '#7c6f5e' : '#f0f0f0',
                            color: viewMode === 'list' ? 'white' : '#555',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          <List size={16} />
                        </button>
                        <button
                          onClick={() => setViewMode('calendar')}
                          style={{
                            padding: '0.4rem',
                            background: viewMode === 'calendar' ? '#7c6f5e' : '#f0f0f0',
                            color: viewMode === 'calendar' ? 'white' : '#555',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          <Grid size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Add button */}
                  <button
                    onClick={() => setShowAddPeriod(!showAddPeriod)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.5rem 1rem',
                      background: '#7c6f5e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      fontSize: '0.9rem'
                    }}
                  >
                    <Plus size={16} />
                    Tilføj
                  </button>
                </div>

                {/* Add Period Form */}
                {showAddPeriod && (
                  <div style={{
                    background: '#f8f8f8',
                    padding: '1.25rem',
                    borderRadius: '10px',
                    marginBottom: '1.25rem',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', color: '#666' }}>Start Dato</label>
                        <input
                          type="date"
                          value={newPeriod.start_date}
                          onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', color: '#666' }}>Antal Dage</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {[2, 3, 4].map(days => (
                            <button
                              key={days}
                              type="button"
                              onClick={() => setNewPeriod({ ...newPeriod, num_days: days })}
                              style={{
                                flex: 1,
                                padding: '0.5rem',
                                background: newPeriod.num_days === days ? '#7c6f5e' : 'white',
                                color: newPeriod.num_days === days ? 'white' : '#333',
                                border: newPeriod.num_days === days ? '2px solid #7c6f5e' : '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: newPeriod.num_days === days ? 600 : 400,
                                fontSize: '0.9rem'
                              }}
                            >
                              {days}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', color: '#666' }}>Beskrivelse (valgfrit)</label>
                        <input
                          type="text"
                          placeholder="Auto-genereret hvis tom"
                          value={newPeriod.description}
                          onChange={(e) => setNewPeriod({ ...newPeriod, description: e.target.value })}
                          style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={handleAddPeriod}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Gem
                        </button>
                        <button
                          onClick={() => setShowAddPeriod(false)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            background: '#e0e0e0',
                            color: '#333',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Luk
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Periods Grid by Month */}
                {monthKeys.length === 0 ? (
                  <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>
                    Ingen dato perioder {periodFilter === 'open' ? 'er åbne' : 'oprettet endnu'}.
                  </p>
                ) : viewMode === 'calendar' ? (
                  /* Calendar View */
                  <div>
                    {isMobile ? (
                      /* Mobile Calendar with Swipe */
                      <div 
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={() => handleTouchEnd(getCalendarMonths())}
                        style={{ overflow: 'hidden' }}
                      >
                        {/* Month navigation for mobile */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '1rem',
                          padding: '0 0.5rem'
                        }}>
                          <button
                            onClick={() => setCurrentMonthIndex(Math.max(0, currentMonthIndex - 1))}
                            disabled={currentMonthIndex === 0}
                            style={{
                              padding: '0.5rem',
                              background: currentMonthIndex === 0 ? '#f5f5f5' : '#7c6f5e',
                              color: currentMonthIndex === 0 ? '#ccc' : 'white',
                              border: 'none',
                              borderRadius: '50%',
                              cursor: currentMonthIndex === 0 ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <ChevronLeft size={20} />
                          </button>
                          <span style={{ 
                            fontWeight: 600, 
                            fontSize: '1.1rem', 
                            color: '#333',
                            textTransform: 'capitalize'
                          }}>
                            {getCalendarMonths()[currentMonthIndex]?.name || ''}
                          </span>
                          <button
                            onClick={() => setCurrentMonthIndex(Math.min(getCalendarMonths().length - 1, currentMonthIndex + 1))}
                            disabled={currentMonthIndex >= getCalendarMonths().length - 1}
                            style={{
                              padding: '0.5rem',
                              background: currentMonthIndex >= getCalendarMonths().length - 1 ? '#f5f5f5' : '#7c6f5e',
                              color: currentMonthIndex >= getCalendarMonths().length - 1 ? '#ccc' : 'white',
                              border: 'none',
                              borderRadius: '50%',
                              cursor: currentMonthIndex >= getCalendarMonths().length - 1 ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <ChevronRight size={20} />
                          </button>
                        </div>
                        
                        {/* Swipe hint */}
                        <p style={{ 
                          textAlign: 'center', 
                          fontSize: '0.75rem', 
                          color: '#999', 
                          margin: '0 0 0.75rem 0' 
                        }}>
                          ← Swipe for at skifte måned →
                        </p>
                        
                        {/* Single month calendar */}
                        {(() => {
                          const calendarMonths = getCalendarMonths();
                          const currentMonth = calendarMonths[currentMonthIndex];
                          if (!currentMonth) return null;
                          
                          const days = generateCalendarDays(currentMonth.year, currentMonth.month);
                          const weekDays = ['Ma', 'Ti', 'On', 'To', 'Fr', 'Lø', 'Sø'];
                          
                          return (
                            <div style={{
                              background: '#fafafa',
                              borderRadius: '12px',
                              padding: '1rem',
                              border: '1px solid #eee'
                            }}>
                              {/* Weekday headers */}
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(7, 1fr)', 
                                gap: '0.25rem',
                                marginBottom: '0.5rem'
                              }}>
                                {weekDays.map(day => (
                                  <div key={day} style={{ 
                                    textAlign: 'center', 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600, 
                                    color: '#888',
                                    padding: '0.25rem'
                                  }}>
                                    {day}
                                  </div>
                                ))}
                              </div>
                              
                              {/* Calendar days */}
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(7, 1fr)', 
                                gap: '0.25rem'
                              }}>
                                {days.map((dayObj, idx) => {
                                  const status = getDateStatus(dayObj.date);
                                  const isToday = dayObj.date === new Date().toISOString().split('T')[0];
                                  
                                  return (
                                    <div 
                                      key={idx}
                                      style={{
                                        aspectRatio: '1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.85rem',
                                        fontWeight: status ? 600 : 400,
                                        borderRadius: '8px',
                                        background: status?.isBooked 
                                          ? '#ffcdd2' 
                                          : status?.isOpen 
                                            ? '#c8e6c9' 
                                            : isToday 
                                              ? '#e3f2fd' 
                                              : 'transparent',
                                        color: dayObj.day 
                                          ? (status?.isBooked ? '#c62828' : status?.isOpen ? '#2e7d32' : '#333')
                                          : 'transparent',
                                        border: isToday ? '2px solid #1976d2' : status ? '1px solid rgba(0,0,0,0.1)' : 'none',
                                        cursor: status ? 'pointer' : 'default'
                                      }}
                                      title={status ? `${status.period.description || formatDate(status.period.start_date)} (${status.isBooked ? 'Booket' : 'Ledig'})` : ''}
                                    >
                                      {dayObj.day || ''}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {/* Legend */}
                              <div style={{ 
                                display: 'flex', 
                                gap: '1rem', 
                                justifyContent: 'center',
                                marginTop: '1rem',
                                fontSize: '0.75rem'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#c8e6c9' }} />
                                  <span>Ledig</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#ffcdd2' }} />
                                  <span>Booket</span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      /* Desktop Calendar Grid */
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: `repeat(${Math.min(monthsView, 3)}, 1fr)`,
                        gap: '1rem'
                      }}>
                        {getCalendarMonths().slice(0, monthsView * 2).map((calMonth) => {
                          const days = generateCalendarDays(calMonth.year, calMonth.month);
                          const weekDays = ['Ma', 'Ti', 'On', 'To', 'Fr', 'Lø', 'Sø'];
                          
                          return (
                            <div key={calMonth.key} style={{
                              background: '#fafafa',
                              borderRadius: '10px',
                              padding: '1rem',
                              border: '1px solid #eee'
                            }}>
                              <h4 style={{ 
                                margin: '0 0 0.75rem 0', 
                                fontSize: '0.95rem', 
                                color: '#7c6f5e',
                                textTransform: 'capitalize',
                                borderBottom: '1px solid #e0e0e0',
                                paddingBottom: '0.5rem',
                                textAlign: 'center'
                              }}>
                                {calMonth.name}
                              </h4>
                              
                              {/* Weekday headers */}
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(7, 1fr)', 
                                gap: '2px',
                                marginBottom: '0.25rem'
                              }}>
                                {weekDays.map(day => (
                                  <div key={day} style={{ 
                                    textAlign: 'center', 
                                    fontSize: '0.65rem', 
                                    fontWeight: 600, 
                                    color: '#888'
                                  }}>
                                    {day}
                                  </div>
                                ))}
                              </div>
                              
                              {/* Calendar days */}
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(7, 1fr)', 
                                gap: '2px'
                              }}>
                                {days.map((dayObj, idx) => {
                                  const status = getDateStatus(dayObj.date);
                                  const isToday = dayObj.date === new Date().toISOString().split('T')[0];
                                  
                                  return (
                                    <div 
                                      key={idx}
                                      style={{
                                        aspectRatio: '1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.7rem',
                                        fontWeight: status ? 600 : 400,
                                        borderRadius: '4px',
                                        background: status?.isBooked 
                                          ? '#ffcdd2' 
                                          : status?.isOpen 
                                            ? '#c8e6c9' 
                                            : isToday 
                                              ? '#e3f2fd' 
                                              : 'transparent',
                                        color: dayObj.day 
                                          ? (status?.isBooked ? '#c62828' : status?.isOpen ? '#2e7d32' : '#333')
                                          : 'transparent',
                                        border: isToday ? '1px solid #1976d2' : 'none',
                                        cursor: status ? 'pointer' : 'default'
                                      }}
                                      title={status ? `${status.period.description || formatDate(status.period.start_date)} (${status.isBooked ? 'Booket' : 'Ledig'})` : ''}
                                    >
                                      {dayObj.day || ''}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Legend for desktop */}
                    {!isMobile && (
                      <div style={{ 
                        display: 'flex', 
                        gap: '1.5rem', 
                        justifyContent: 'center',
                        marginTop: '1rem',
                        fontSize: '0.8rem',
                        color: '#666'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <div style={{ width: '14px', height: '14px', borderRadius: '4px', background: '#c8e6c9' }} />
                          <span>Ledig periode</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <div style={{ width: '14px', height: '14px', borderRadius: '4px', background: '#ffcdd2' }} />
                          <span>Booket</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <div style={{ width: '14px', height: '14px', borderRadius: '4px', background: '#e3f2fd', border: '1px solid #1976d2' }} />
                          <span>I dag</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* List View */
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : `repeat(${monthsView}, 1fr)`,
                    gap: '1rem'
                  }}>
                    {/* On mobile: show all months. On desktop: show monthsView*2 months */}
                    {(isMobile ? monthKeys : monthKeys.slice(0, monthsView * 2)).map((monthKey) => (
                      <div key={monthKey} style={{
                        background: '#fafafa',
                        borderRadius: '10px',
                        padding: '1rem',
                        border: '1px solid #eee'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 0.75rem 0', 
                          fontSize: '0.95rem', 
                          color: '#7c6f5e',
                          textTransform: 'capitalize',
                          borderBottom: '1px solid #e0e0e0',
                          paddingBottom: '0.5rem'
                        }}>
                          {groupedPeriods[monthKey].name}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {groupedPeriods[monthKey].periods.map((period) => {
                            const status = getPeriodBookingStatus(period.id);
                            return (
                              <div
                                key={period.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.6rem 0.75rem',
                                  background: status.isFullyBooked ? '#ffebee' : (period.is_active ? '#e8f5e9' : '#f5f5f5'),
                                  borderRadius: '6px',
                                  border: status.isFullyBooked ? '1px solid #ffcdd2' : (period.is_active ? '1px solid #c8e6c9' : '1px solid #e0e0e0'),
                                  opacity: period.is_active ? 1 : 0.6,
                                  fontSize: '0.85rem'
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 500, color: '#333' }}>
                                    {formatDate(period.start_date)} - {formatDate(period.end_date)}
                                  </div>
                                  {status.approvedCount > 0 && (
                                    <span style={{ 
                                      fontSize: '0.75rem', 
                                      color: '#c62828',
                                      fontWeight: 600
                                    }}>
                                      BOOKET
                                    </span>
                                  )}
                                  {!status.isFullyBooked && period.is_active && (
                                    <span style={{ 
                                      fontSize: '0.75rem', 
                                      color: '#2e7d32'
                                    }}>
                                      Åben
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  <button
                                    onClick={() => handleTogglePeriod(period.id, period.is_active)}
                                    title={period.is_active ? 'Deaktiver' : 'Aktiver'}
                                    style={{
                                      padding: '0.25rem 0.4rem',
                                      background: period.is_active ? '#ff9800' : '#4caf50',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '0.7rem'
                                    }}
                                  >
                                    {period.is_active ? 'Off' : 'On'}
                                  </button>
                                  <button
                                    onClick={() => handleDeletePeriod(period.id)}
                                    title="Slet"
                                    style={{
                                      padding: '0.25rem 0.35rem',
                                      background: '#f44336',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Course Requests Section - Always visible */}
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{ 
              fontSize: '1.2rem', 
              marginBottom: '1.25rem', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem' 
            }}>
              <FileText size={22} color="#7c6f5e" />
              Kursus Anmodninger
              {weekendCourses.filter(c => c.status === 'requested').length > 0 && (
                <span style={{
                  background: '#ff9800',
                  color: 'white',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  marginLeft: '0.25rem'
                }}>
                  {weekendCourses.filter(c => c.status === 'requested').length} ny(e)
                </span>
              )}
            </h2>

            {loading ? (
              <LoadingSkeleton type="table" count={3} />
            ) : weekendCourses.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>
                Ingen kursusanmodninger endnu.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {weekendCourses.map((course) => (
                  <div
                    key={course.id}
                    style={{
                      border: '1px solid #e5e5e5',
                      borderRadius: '10px',
                      padding: '1.25rem',
                      background: course.status === 'requested' ? '#fffde7' : '#fff'
                    }}
                  >
                    {/* Header Row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>{course.stable_name || course.client_name}</h3>
                        <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                          {course.client_name} • {course.client_email}
                          {course.client_phone && ` • ${course.client_phone}`}
                        </p>
                      </div>
                      {getStatusBadge(course.status)}
                    </div>

                    {/* Details */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                      gap: '0.75rem',
                      marginBottom: '0.75rem',
                      padding: '0.75rem',
                      background: '#f8f8f8',
                      borderRadius: '6px',
                      fontSize: '0.85rem'
                    }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Dage</div>
                        <div style={{ fontWeight: 600 }}>{course.num_days} dage</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Adresse</div>
                        <div style={{ fontWeight: 500 }}>{course.stable_address || '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Type</div>
                        <div>{course.course_type === 'series' ? 'Kursusrække' : 'Enkeltkursus'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Ønsket Dato</div>
                        <div style={{ fontWeight: 500 }}>
                          {course.selected_date_period ? getPeriodDescription(course.selected_date_period) : '-'}
                          {course.day_preference && (
                            <span style={{ color: '#888', fontSize: '0.75rem' }}>
                              {' '}({course.day_preference === 'first' ? 'Første' : 'Sidste'} {course.num_days})
                            </span>
                          )}
                        </div>
                      </div>
                      {course.alternative_date_period && (
                        <div>
                          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Alternativ</div>
                          <div>{getPeriodDescription(course.alternative_date_period)}</div>
                        </div>
                      )}
                      {course.assigned_dates && (
                        <div>
                          <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Tildelt</div>
                          <div style={{ fontWeight: 600, color: '#2e7d32' }}>{course.assigned_dates}</div>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {course.additional_notes && (
                      <div style={{ 
                        padding: '0.6rem 0.75rem', 
                        background: '#e3f2fd', 
                        borderRadius: '6px',
                        marginBottom: '0.75rem',
                        fontSize: '0.85rem'
                      }}>
                        <strong>Note:</strong> {course.additional_notes}
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {course.status === 'requested' && (
                        <>
                          <button
                            onClick={() => handleUpdateCourseStatus(course.id, 'processed')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#1976d2', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <Clock size={14} /> Behandlet
                          </button>
                          <button
                            onClick={() => openAcceptModal(course)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#4caf50', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <Check size={14} /> Godkend
                          </button>
                          <button
                            onClick={() => openAcceptModalWithCustomDate(course)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#ff9800', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <Calendar size={14} /> Anden dato
                          </button>
                          <button
                            onClick={() => handleUpdateCourseStatus(course.id, 'rejected')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#f44336', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <X size={14} /> Afvis
                          </button>
                        </>
                      )}
                      {course.status === 'processed' && (
                        <>
                          <button
                            onClick={() => openAcceptModal(course)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#4caf50', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <Check size={14} /> Godkend
                          </button>
                          <button
                            onClick={() => openAcceptModalWithCustomDate(course)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#ff9800', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <Calendar size={14} /> Anden dato
                          </button>
                          <button
                            onClick={() => handleUpdateCourseStatus(course.id, 'rejected')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.4rem 0.75rem', background: '#f44336', color: 'white',
                              border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                            }}
                          >
                            <X size={14} /> Afvis
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteCourse(course.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.4rem 0.75rem', background: '#9e9e9e', color: 'white',
                          border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem'
                        }}
                      >
                        <Trash2 size={14} /> Slet
                      </button>
                    </div>

                    {/* Timestamp */}
                    <div style={{ marginTop: '0.6rem', fontSize: '0.75rem', color: '#999' }}>
                      Modtaget: {new Date(course.created_at).toLocaleString('da-DK')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Dates Modal */}
      {assignDatesModal.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '1.5rem', borderRadius: '12px',
            maxWidth: '500px', width: '90%'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>Accepter Kursus</h3>
            
            {/* Customer request info */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Stald/Klub:</strong> {assignDatesModal.course?.stable_name}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Antal dage:</strong> {assignDatesModal.course?.num_days} dage
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Ønsket periode:</strong> {getPeriodDescription(assignDatesModal.course?.selected_date_period)}
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Dag præference:</strong> {assignDatesModal.course?.day_preference === 'first' 
                  ? `Første ${assignDatesModal.course?.num_days} dage` 
                  : `Sidste ${assignDatesModal.course?.num_days} dage`}
              </div>
              {assignDatesModal.course?.alternative_date_period && (
                <div style={{ color: '#666' }}>
                  <strong>Alternativ:</strong> {getPeriodDescription(assignDatesModal.course?.alternative_date_period)}
                  {assignDatesModal.course?.alternative_day_preference && (
                    <span> ({assignDatesModal.course?.alternative_day_preference === 'first' ? 'Første' : 'Sidste'} dage)</span>
                  )}
                </div>
              )}
            </div>

            {/* Date input */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#333', fontWeight: 600 }}>
                Bekræftede Datoer
              </label>
              
              {/* Show date pickers when no pre-filled dates (custom date mode) */}
              {!assignDatesModal.bookedStartDate && (
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
                    Vælg start- og slutdato for kurset:
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#888' }}>Start dato</label>
                      <input
                        type="date"
                        value={assignDatesModal.bookedStartDate}
                        onChange={(e) => {
                          const start = e.target.value;
                          const numDays = assignDatesModal.course?.num_days || 2;
                          const startDate = new Date(start + 'T00:00:00');
                          const endDate = new Date(startDate);
                          endDate.setDate(startDate.getDate() + numDays - 1);
                          const endStr = endDate.toISOString().split('T')[0];
                          
                          // Generate display text
                          const startDay = startDate.getDate();
                          const endDay = endDate.getDate();
                          const month = endDate.toLocaleDateString('da-DK', { month: 'short' });
                          const year = endDate.getFullYear();
                          const displayText = `${startDay}-${endDay}. ${month} ${year}`;
                          
                          setAssignDatesModal({ 
                            ...assignDatesModal, 
                            bookedStartDate: start, 
                            bookedEndDate: endStr,
                            dates: displayText
                          });
                        }}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#888' }}>Slut dato (auto-beregnet)</label>
                      <input
                        type="date"
                        value={assignDatesModal.bookedEndDate}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', background: '#f5f5f5' }}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {assignDatesModal.bookedStartDate && (
                <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
                  Feltet er udfyldt med kundens ønske. Ret til hvis nødvendigt.
                </p>
              )}
              
              <input
                type="text"
                value={assignDatesModal.dates}
                onChange={(e) => setAssignDatesModal({ ...assignDatesModal, dates: e.target.value })}
                placeholder="F.eks. 13-16. aug. 2026"
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  borderRadius: '6px', 
                  border: '2px solid #7c6f5e',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAssignDatesModal({ show: false, course: null, dates: '', bookedStartDate: '', bookedEndDate: '' })}
                style={{
                  padding: '0.6rem 1.2rem', background: '#e0e0e0', color: '#333',
                  border: 'none', borderRadius: '6px', cursor: 'pointer'
                }}
              >
                Annuller
              </button>
              <button
                onClick={() => handleUpdateCourseStatus(
                  assignDatesModal.course.id, 
                  'approved', 
                  assignDatesModal.dates,
                  assignDatesModal.bookedStartDate,
                  assignDatesModal.bookedEndDate
                )}
                disabled={!assignDatesModal.dates}
                style={{
                  padding: '0.6rem 1.2rem', 
                  background: assignDatesModal.dates ? '#4caf50' : '#ccc', 
                  color: 'white',
                  border: 'none', borderRadius: '6px', cursor: assignDatesModal.dates ? 'pointer' : 'not-allowed'
                }}
              >
                Godkend Kursus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .admin-container {
            padding: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminWeekendCoursesPage;
